// src/pages/TemplatesPage.jsx
import { useEffect, useState, useRef } from "react";
import TemplatePlantillaPro from "../components/TemplatePlantillaPro";
import api from "../auth/api";

export default function TemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [cloudAccounts, setCloudAccounts] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  // Campos de edición
  const [name, setName] = useState("");
  const [columns, setColumns] = useState([]); // si no tienes dinámicas, deja []
  const [sendEmail, setSendEmail] = useState(false);
  const [templateEmailSubject, setTemplateEmailSubject] = useState("");
  const [templateEmailBody, setTemplateEmailBody] = useState("");

  // NUEVO: lista de mensajes WA de la plantilla actual
  const [whatsappTemplates, setWhatsappTemplates] = useState([]);

  // Modal
  const [openEditor, setOpenEditor] = useState(false);
  const isEditing = !!selectedId;

  // Flags que controlan pestañas / comportamiento al abrir el editor
  const [isWhatsApp, setIsWhatsApp] = useState(undefined);
  const [isEmail, setIsEmail] = useState(undefined);
  const [isWhatsAppBusiness, setIsWhatsAppBusiness] = useState(false);
  const [selectedCloudAccountId, setSelectedCloudAccountId] = useState("");

  // Menu Nuevo (simple)
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const load = async () => {
    const r = await api.get("/templates");
    setTemplates(r.data.templates || []);
  };
  const loadCloudAccounts = async () => {
    const r = await api.get("/wa/accounts");
    setCloudAccounts((r?.data?.rows || []).filter((a) => a.provider === "cloud"));
  };

  useEffect(() => {
    load();
    loadCloudAccounts().catch(() => setCloudAccounts([]));
  }, []);


  /* ---------------------------
  Helpers para aprobación / UI
   --------------------------- */
  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleString();
    } catch {
      return iso;
    }
  };

  const requestApproval = async (templateId) => {
    try {
      const tpl = templates.find((t) => String(t.id) === String(templateId));
      if (!tpl?.meta_wa_account_id) {
        alert("Edita la plantilla y selecciona la cuenta Cloud/WABA destino antes de enviarla a Meta.");
        return;
      }
      const r = await api.post(`/templates/${templateId}/meta/submit`, {
        wa_account_id: tpl.meta_wa_account_id,
        category: "MARKETING",
        language: "es",
      });
      if (r.data && r.data.ok) {
        alert("Plantilla enviada a Meta. Queda pendiente hasta que Meta la apruebe.");
        await load();
      } else {
        alert("No se pudo enviar la plantilla a Meta.");
      }
    } catch (e) {
      console.error("requestApproval error:", e);
      const detail = e?.response?.data?.detail || e?.response?.data?.error || e?.message || "";
      alert(`Error al solicitar aprobación en Meta.\n${detail}`);
    }
  };

  const syncMetaTemplate = async (templateId) => {
    try {
      const tpl = templates.find((t) => String(t.id) === String(templateId));
      const r = await api.post(`/templates/${templateId}/meta/sync`, {
        wa_account_id: tpl?.meta_wa_account_id || null,
      });
      const status = r?.data?.status || r?.data?.meta?.status || "sin estado";
      alert(`Estado actualizado desde Meta: ${status}`);
      await load();
    } catch (e) {
      console.error("syncMetaTemplate error:", e);
      const detail = e?.response?.data?.detail || e?.response?.data?.error || e?.message || "";
      alert(`No se pudo sincronizar con Meta.\n${detail}`);
    }
  };

  const approveTemplate = async (templateId) => {
    try {
      // Obtener la lista de approvals y buscar la más reciente en 'pending'
      const r = await api.get(`/templates/${templateId}/approvals`);
      const approvals = r.data.approvals || [];
      const pending = approvals.find(a => a.status === "pending");

      if (!pending) {
        alert("No hay solicitudes pendientes para aprobar.");
        return;
      }

      // Intentar aprobar (requiere permisos de admin en backend)
      const approveResp = await api.post(`/templates/approvals/${pending.id}/approve`, { review_notes: "Aprobado desde UI" });
      if (approveResp.data && approveResp.data.ok) {
        alert("Plantilla aprobada.");
        await load();
      } else {
        alert("No se pudo aprobar la plantilla.");
      }
    } catch (e) {
      console.error("approveTemplate error:", e);
      // mostrar mensaje según status HTTP
      if (e.response && e.response.status === 403) {
        alert("No tienes permisos para aprobar. Requiere rol de admin.");
      } else {
        alert("Error al aprobar plantilla. Revisa la consola.");
      }
    }
  };



  // Cerrar menu si clic afuera
  useEffect(() => {
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const resetForm = () => {
    setSelectedId(null);
    setName("");
    setSendEmail(false);
    setTemplateEmailSubject("");
    setTemplateEmailBody("");
    setWhatsappTemplates([]);
    // reset flags a undefined para comportamiento por defecto (ambas pestañas)
    setIsWhatsApp(undefined);
    setIsEmail(undefined);
    setIsWhatsAppBusiness(false);
    setSelectedCloudAccountId("");
  };

  // Open create generic (both tabs)
  const openCreate = () => {
    resetForm();
    // default: una plantilla WA vacía (útil)
    setWhatsappTemplates([
      {
        id: 1,
        name: "Mensaje principal",
        text: "",
        delay: 0,
      },
    ]);
    // por defecto mantener visible ambas pestañas
    setIsWhatsApp(undefined);
    setIsEmail(undefined);
    setIsWhatsAppBusiness(false);
    setOpenEditor(true);
  };

  // Open create - WhatsApp Web (Baileys)
  const openCreateWAWeb = () => {
    resetForm();
    setWhatsappTemplates([
      {
        id: 1,
        name: "Mensaje principal",
        text: "",
        delay: 0,
      },
    ]);
    setIsWhatsApp(true);
    setIsEmail(false);
    setIsWhatsAppBusiness(false); // web => not business api
    setOpenEditor(true);
    setMenuOpen(false);
  };

  // Open create - WhatsApp API (Business -> pending approval flow)
  const openCreateWAApi = () => {
    resetForm();
    setWhatsappTemplates([
      {
        id: 1,
        name: "Mensaje principal",
        text: "",
        delay: 0,
      },
    ]);
    setIsWhatsApp(true);
    setIsEmail(false);
    setIsWhatsAppBusiness(true); // marks as business api -> pending_approval
    setSelectedCloudAccountId(cloudAccounts[0]?.id ? String(cloudAccounts[0].id) : "");
    setOpenEditor(true);
    setMenuOpen(false);
  };

  // Open create - Email only
  const openCreateEmail = () => {
    resetForm();
    setSendEmail(true);
    setTemplateEmailSubject("");
    setTemplateEmailBody("");
    setIsWhatsApp(false);
    setIsEmail(true);
    setIsWhatsAppBusiness(false);
    setOpenEditor(true);
    setMenuOpen(false);
  };

  const openEdit = (t) => {
    setSelectedId(t.id || null);
    setName(t.name || "");
    const isEmailFlag = !!t.is_email;
    setSendEmail(isEmailFlag);
    setTemplateEmailSubject(t.email_subject || "");
    setTemplateEmailBody(t.content_email || "");

    // Set flags from template (if present)
    setIsWhatsApp(t.is_whatsapp === undefined ? undefined : Boolean(t.is_whatsapp));
    setIsEmail(t.is_email === undefined ? undefined : Boolean(t.is_email));
    setIsWhatsAppBusiness(t.is_whatsapp_business === undefined ? false : Boolean(t.is_whatsapp_business));
    setSelectedCloudAccountId(t.meta_wa_account_id ? String(t.meta_wa_account_id) : "");

    let waList = [];

    // 1) Si viene whatsapp_templates, lo usamos
    if (t.whatsapp_templates) {
      try {
        const parsed =
          typeof t.whatsapp_templates === "string"
            ? JSON.parse(t.whatsapp_templates)
            : t.whatsapp_templates;

        if (Array.isArray(parsed) && parsed.length) {
          waList = parsed.map((msg, idx) => ({
            id: msg.id ?? idx + 1,
            name: msg.name || `Mensaje ${idx + 1}`,
            text: msg.text || msg.template || "",
            delay: msg.delay ?? 0,
          }));
        }
      } catch (e) {
        console.warn("No se pudo parsear whatsapp_templates:", e);
      }
    }

    // 2) Backwards compatibility / comportamiento deseado:
    // - Si NO hay lista y NO es solo email: asumimos plantilla vieja de WhatsApp y usamos t.content
    // - Si NO hay lista y SÍ es solo email: dejamos WhatsApp vacío
    if (!waList.length) {
      if (!isEmailFlag && (t.content || "").trim()) {
        // Plantilla antigua: content era el texto de WhatsApp
        waList = [
          {
            id: 1,
            name: "Mensaje principal",
            text: t.content || "",
            delay: 0,
          },
        ];
      } else {
        // Solo email o no había contenido WA => NO rellenar con nada
        waList = [
          {
            id: 1,
            name: "Mensaje principal",
            text: "",
            delay: 0,
          },
        ];
      }
    }

    setWhatsappTemplates(waList);
    setOpenEditor(true);
  };

  // ⬇⬇⬇ save recibe "data" desde TemplatePlantillaPro cuando se edita/crea desde el modal
  const save = async (data) => {
    // si data viene desde el componente, lo usamos (ahí ya vienen flags como is_whatsapp, is_whatsapp_business)
    // si no, construimos desde los states locales.
    const payload =
      data || {
        name,
        whatsappTemplates,
        email: {
          enabled: sendEmail,
          subject: templateEmailSubject,
          body: templateEmailBody,
        },
        is_whatsapp: isWhatsApp === undefined ? (whatsappTemplates && whatsappTemplates.some(m => (m.text || "").trim())) : (isWhatsApp ? 1 : 0),
        is_email: isEmail === undefined ? (sendEmail ? 1 : 0) : (isEmail ? 1 : 0),
        is_whatsapp_business: isWhatsAppBusiness ? 1 : 0,
      };

    const {
      name: pName,
      whatsappTemplates: waList = [],
      email,
      is_whatsapp: payloadIsWhatsApp,
      is_whatsapp_business: payloadIsWhatsAppBusiness,
      is_email: payloadIsEmail,
    } = payload;

    // Normalizamos
    const trimmedName = (pName || "").trim();

    // ¿Hay al menos un mensaje de WhatsApp con texto?
    const hasWhatsApp =
      Array.isArray(waList) &&
      waList.some((msg) => (msg.text || msg.template || "").trim() !== "");

    // ¿Hay email configurado y con cuerpo?
    const hasEmail =
      !!(email?.enabled) &&
      !!(email.body && email.body.toString().trim() !== "");

    // Validaciones
    if (!trimmedName) {
      alert("El nombre de la plantilla es obligatorio.");
      return;
    }

    if (!hasWhatsApp && !hasEmail) {
      alert("Debes configurar al menos WhatsApp o Email para guardar la plantilla.");
      return;
    }

    // Elegimos un "contenido principal" para compatibilidad:
    let mainContent = "";

    if (hasWhatsApp) {
      const firstMsg = waList.find(
        (msg) => (msg.text || msg.template || "").trim() !== ""
      );
      mainContent = (firstMsg?.text || firstMsg?.template || "").trim();
    } else if (hasEmail) {
      // mainContent left empty for email-only
    }

    // Serializamos solo si realmente hay WhatsApp configurado
    const waJson = hasWhatsApp ? JSON.stringify(waList) : null;

    // Determinar flags definitivos (prioridad: payload flags > local detection)
    const finalIsWhatsApp = payloadIsWhatsApp !== undefined ? Number(!!payloadIsWhatsApp) : (hasWhatsApp ? 1 : 0);
    const finalIsWhatsAppBusiness = payloadIsWhatsAppBusiness !== undefined ? Number(!!payloadIsWhatsAppBusiness) : (isWhatsAppBusiness ? 1 : 0);
    const finalIsEmail = payloadIsEmail !== undefined ? Number(!!payloadIsEmail) : (hasEmail ? 1 : 0);
    const finalMetaWaAccountId = payload.meta_wa_account_id || selectedCloudAccountId || null;

    if (finalIsWhatsAppBusiness && !finalMetaWaAccountId) {
      alert("Selecciona la cuenta Cloud/WABA destino para esta plantilla.");
      return;
    }

    const dto = {
      name: trimmedName,
      content: mainContent, // compatibilidad backend
      whatsapp_templates: waJson,
      content_email: hasEmail ? email.body : "",
      email_subject: hasEmail ? (email.subject || "") : "",
      is_email: finalIsEmail,
      is_whatsapp: finalIsWhatsApp,
      is_whatsapp_business: finalIsWhatsAppBusiness,
      meta_wa_account_id: finalIsWhatsAppBusiness ? Number(finalMetaWaAccountId) : null,
    };

    try {
      if (isEditing) {
        await api.put(`/templates/${selectedId}`, dto);
        alert("Plantilla actualizada.");
      } else {
        await api.post("/templates", dto);
        alert("Plantilla creada.");
      }
      await load();
      setOpenEditor(false);
      resetForm();
    } catch (e) {
      console.error("Error guardando plantilla:", e);
      alert("Error al guardar la plantilla. Revisa la consola.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar plantilla seleccionada?")) return;
    await api.delete(`/templates/${id}`);
    await load();
  };

  /* helpers (dentro del componente TemplatesPage) */
  const renderChannelBadges = (t) => {
    const isWhatsApp = Number(t.is_whatsapp) === 1;
    const isEmailTpl = Number(t.is_email) === 1;
    const isWsBusiness = Number(t.is_whatsapp_business) === 1;

    return (
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        {isWhatsApp && !isWsBusiness && (
          <span className="badge badge--channel badge--channel--whatsapp" title="WhatsApp (Web)">
            <span className="icon">💬</span> WhatsApp
          </span>
        )}
        {isWhatsApp && isWsBusiness && (
          <span className="badge badge--channel badge--channel--whatsapp-api" title="WhatsApp Business API">
            <span className="icon">🔗</span> WhatsApp API
          </span>
        )}
        {isEmailTpl && (
          <span className="badge badge--channel badge--channel--email" title="Email">
            <span className="icon">✉️</span> Email
          </span>
        )}
        {!isWhatsApp && !isEmailTpl && (
          <small style={{ color: "var(--muted)" }}>Sin canal</small>
        )}
      </div>
    );
  };

  const renderStatusBadge = (status) => {
    const s = (status || "draft").toString().toLowerCase();
    // mapear posibles valores a clases válidas (evitar caracteres raros)
    const cls = `badge--status badge--status-${s.replace(/\s+/g, "_")}`;
    return <span className={cls}>{s}</span>;
  };


  return (
    <>
      <div
        className="page-title"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <span>Plantillas</span>

        {/* Botón Nuevo con menú */}
        <div style={{ position: "relative" }} ref={menuRef}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            {/* <button
              className="btn btn-primary"
              onClick={openCreate}
              title="Nueva plantilla"
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <svg
                  width="8"
                  height="8"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden
                >
                  <path d="M12 5c.552 0 1 .448 1 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H6a1 1 0 110-2h5V6c0-.552.448-1 1-1z" />
                </svg>
                Nuevo
              </span>
            </button>*/}

            <button
              className="btn btn-primary"
              style={{ padding: "6px 8px" }}
              onClick={() => setMenuOpen((s) => !s)}
              aria-expanded={menuOpen}
              aria-haspopup="true"
              title="Más opciones"
            >Opciones
              ▾
            </button>
          </div>

          {menuOpen && (
            <div
              style={{
                position: "absolute",
                right: 0,
                marginTop: 8,
                background: "var(--panel-bg, #0b1220)",
                border: "1px solid rgba(255,255,255,0.04)",
                borderRadius: 8,
                boxShadow: "0 6px 20px rgba(2,6,23,0.6)",
                zIndex: 60,
                minWidth: 210,
                padding: 8,
                fontSize: "12px" // ← aquí reduces el tamaño
              }}
            >
              <button className="btn" style={{ width: "100%", textAlign: "left", marginBottom: 6 }} onClick={openCreateWAWeb}>
                Nueva — WhatsApp (Web / Baileys)
              </button>
              <button className="btn" style={{ width: "100%", textAlign: "left", marginBottom: 6 }} onClick={openCreateWAApi}>
                Nueva — WhatsApp (Business API)
              </button>
              <button className="btn" style={{ width: "100%", textAlign: "left" }} onClick={openCreateEmail}>
                Nueva — Email
              </button>
            </div>
          )}
        </div>
      </div>

      {/* TABLA A ANCHO COMPLETO */}
      {/* ---------------------------
   Tabla de plantillas (mejorada)
   --------------------------- */}
      <section className="card section">
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%" }}>
            <thead>
              <tr>
                <th>ID</th>
                <th>NOMBRE</th>
                <th>CANAL</th>
                <th>ESTADO</th>
                <th>WABA / CUENTA CLOUD</th>
                {/* CANAL
                <th>CONTENIDO (preview)</th> */}
                <th>APROBADO POR</th>
                <th>CREADA</th>
                <th>ACTUALIZADA</th>
                <th style={{ width: 220 }}></th>
              </tr>
            </thead>
            <tbody>
              {(templates || []).map((t) => {
                // Determinar canales visibles
                const isWhatsApp = Number(t.is_whatsapp) === 1;
                const isEmailTpl = Number(t.is_email) === 1;
                const isWsBusiness = Number(t.is_whatsapp_business) === 1; // si tu backend devuelve esta columna
                // Extraer preview: preferir content (compat) o primer mensaje WA
                let preview = t.content || "";
                try {
                  if ((!preview || preview.trim() === "") && t.whatsapp_templates) {
                    const parsed = typeof t.whatsapp_templates === "string" ? JSON.parse(t.whatsapp_templates) : t.whatsapp_templates;
                    if (Array.isArray(parsed) && parsed.length) {
                      preview = parsed[0].text || parsed[0].template || "";
                    }
                  }
                } catch (e) {
                  console.warn("No se pudo parsear whatsapp_templates preview", e);
                }

                return (
                  <tr key={t.id}>
                    <td>{t.id}</td>
                    <td>{t.name}</td>

                    {/* CANAL */}
                    <td>
                     <td>{renderChannelBadges(t)}</td>
                    </td>

                    {/* STATUS */}
                    <td>{renderStatusBadge(t.status)}</td>

                    <td>
                      {t.meta_wa_account_id ? (
                        <small>
                          {cloudAccounts.find((a) => String(a.id) === String(t.meta_wa_account_id))?.label || `Cuenta #${t.meta_wa_account_id}`}
                          {t.meta_waba_id ? <><br />WABA {t.meta_waba_id}</> : null}
                        </small>
                      ) : (
                        <small style={{ color: "var(--muted)" }}>—</small>
                      )}
                    </td>

                    {/* CONTENT PREVIEW 
                    <td
                      style={{
                        maxWidth: 420,
                        whiteSpace: "pre-wrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={preview}
                    >
                      {preview}
                    </td>*/}

                    {/* approved_by / approved_at */}
                    <td>
                      {t.approved_by ? (
                        <div>
                          <div style={{ fontSize: 13 }}>{t.approved_by}</div>
                          <div style={{ fontSize: 11, color: "#666" }}>{formatDate(t.approved_at)}</div>
                        </div>
                      ) : (
                        <small style={{ color: "var(--muted)" }}>—</small>
                      )}
                    </td>

                    <td>{t.created_at || ""}</td>
                    <td>{t.updated_at || ""}</td>

                    {/* ACCIONES */}
                    <td style={{ display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center" }}>
                      <button className="btn btn-muted" onClick={() => openEdit(t)}>Editar</button>
                      <button className="btn btn-danger" onClick={() => remove(t.id)}>Eliminar</button>

                      {/* Si es plantilla Business y no está aprobada, enviarla a Meta */}
                      {isWhatsApp && isWsBusiness && t.status !== "pending_approval" && t.status !== "approved" && (
                        <button className="btn" title="Solicitar aprobación (Meta)" onClick={() => {
                          if (!window.confirm("Enviar esta plantilla a Meta para aprobación?")) return;
                          requestApproval(t.id);
                        }}>
                          Enviar a Meta
                        </button>
                      )}

                      {isWhatsApp && isWsBusiness && t.status === "pending_approval" && (
                        <button className="btn btn-primary" title="Consultar estado en Meta" onClick={() => syncMetaTemplate(t.id)}>
                          Sincronizar Meta
                        </button>
                      )}

                      {/* Flujo interno solo para plantillas que no son Business API */}
                      {!isWsBusiness && t.status === "pending_approval" && (
                        <button className="btn btn-primary" title="Aprobar (admin)" onClick={() => {
                          if (!window.confirm("Aprobar esta plantilla? (requiere permisos de admin)")) return;
                          approveTemplate(t.id);
                        }}>
                          Aprobar
                        </button>
                      )}

                      {/* Ver historial / versiones */}
                      <button className="btn" onClick={async () => {
                        // abrir página de versiones o modal: por ahora cargar y abrir consola / alert
                        try {
                          const r = await api.get(`/templates/${t.id}/versions`);
                          const versions = r.data.versions || [];
                          // mostrar resumen rápido
                          const summary = versions.map(v => `v${v.version} — ${v.created_at}`).join("\n");
                          alert(summary || "No hay versiones");
                        } catch (e) {
                          console.error("error versiones:", e);
                          alert("Error cargando versiones.");
                        }
                      }}>
                        Versiones
                      </button>
                    </td>
                  </tr>
                );
              })}
              {templates.length === 0 && (
                <tr>
                  <td colSpan={10} style={{ color: "var(--muted)" }}>
                    No hay plantillas aún.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* MODAL Editor (crear/editar) */}
      {openEditor && (
        <TemplatePlantillaPro
          modal
          title={
            isEditing
              ? `Editar: ${name || "(sin nombre)"}`
              : "Nueva plantilla"
          }
          primaryLabel="Guardar"
          secondaryLabel="Cerrar"
          onPrimary={save}
          onSecondary={() => setOpenEditor(false)}
          onClose={() => setOpenEditor(false)}
          // Nombre
          name={name}
          setName={setName}
          // WhatsApp: lista de mensajes
          templatesWa={whatsappTemplates}
          setTemplatesWa={setWhatsappTemplates}
          // Variables disponibles
          columns={columns}
          // Email
          sendEmail={sendEmail}
          setSendEmail={setSendEmail}
          templateEmailSubject={templateEmailSubject}
          setTemplateEmailSubject={setTemplateEmailSubject}
          templateEmailBody={templateEmailBody}
          setTemplateEmailBody={setTemplateEmailBody}
          // Flags para controlar pestañas / comportamiento
          isWhatsApp={isWhatsApp}
          isEmail={isEmail}
          isWhatsAppBusiness={isWhatsAppBusiness}
          cloudAccounts={cloudAccounts}
          selectedCloudAccountId={selectedCloudAccountId}
          setSelectedCloudAccountId={setSelectedCloudAccountId}
        />
      )}
    </>
  );
}
